import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Zap, User, ArrowLeft, Save, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    birth_date: "",
    weight_kg: "",
    height_cm: "",
    gender: ""
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/profile`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.profile) {
          setProfile({
            birth_date: data.profile.birth_date || "",
            weight_kg: data.profile.weight_kg || "",
            height_cm: data.profile.height_cm || "",
            gender: data.profile.gender || ""
          });
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profile.birth_date || !profile.weight_kg || !profile.height_cm || !profile.gender) {
      toast.error("Bitte fülle alle Felder aus");
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/profile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` })
        },
        credentials: "include",
        body: JSON.stringify({
          birth_date: profile.birth_date,
          weight_kg: parseFloat(profile.weight_kg),
          height_cm: parseFloat(profile.height_cm),
          gender: profile.gender
        })
      });

      if (response.ok) {
        toast.success("Profil gespeichert!");
        navigate("/dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Fehler beim Speichern");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-volt"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Zap className="h-8 w-8 text-volt" />
              <span className="font-display font-bold text-xl tracking-tight">VELOMETRICS</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">by Loops Cycling Academy</span>
            </Link>
            <Link to="/dashboard" className="nav-link text-sm font-medium flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Zurück
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="profile-page">
        <div className="max-w-xl mx-auto">
          <div className="mb-8">
            <p className="data-label mb-2">Athletenprofil</p>
            <h1 className="font-display text-3xl font-bold tracking-tight uppercase">
              Deine <span className="text-volt">Daten</span>
            </h1>
            <p className="text-muted-foreground mt-2">
              Diese Informationen werden für die Berechnung deiner Leistungswerte benötigt.
            </p>
          </div>

          <div className="glass-card rounded-xl p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Birth Date */}
              <div>
                <Label htmlFor="birth_date" className="data-label">Geburtsdatum</Label>
                <Input
                  id="birth_date"
                  type="date"
                  value={profile.birth_date}
                  onChange={(e) => setProfile({ ...profile, birth_date: e.target.value })}
                  className="mt-2 bg-zinc-950 border-zinc-800 focus:border-volt h-12"
                  required
                  data-testid="profile-birthdate-input"
                />
              </div>

              {/* Weight */}
              <div>
                <Label htmlFor="weight_kg" className="data-label">Gewicht (kg)</Label>
                <Input
                  id="weight_kg"
                  type="number"
                  step="0.1"
                  min="30"
                  max="200"
                  value={profile.weight_kg}
                  onChange={(e) => setProfile({ ...profile, weight_kg: e.target.value })}
                  placeholder="75"
                  className="mt-2 bg-zinc-950 border-zinc-800 focus:border-volt h-12"
                  required
                  data-testid="profile-weight-input"
                />
              </div>

              {/* Height */}
              <div>
                <Label htmlFor="height_cm" className="data-label">Größe (cm)</Label>
                <Input
                  id="height_cm"
                  type="number"
                  min="100"
                  max="250"
                  value={profile.height_cm}
                  onChange={(e) => setProfile({ ...profile, height_cm: e.target.value })}
                  placeholder="180"
                  className="mt-2 bg-zinc-950 border-zinc-800 focus:border-volt h-12"
                  required
                  data-testid="profile-height-input"
                />
              </div>

              {/* Gender */}
              <div>
                <Label className="data-label">Geschlecht</Label>
                <Select 
                  value={profile.gender} 
                  onValueChange={(value) => setProfile({ ...profile, gender: value })}
                >
                  <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800 focus:border-volt h-12" data-testid="profile-gender-select">
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Männlich</SelectItem>
                    <SelectItem value="female">Weiblich</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="w-full skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider h-12"
                data-testid="profile-save-btn"
              >
                <span className="flex items-center gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Speichern
                </span>
              </Button>
            </form>
          </div>

          {/* User Info */}
          <div className="mt-8 glass-card rounded-xl p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-volt/10 flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="h-12 w-12 rounded-full" />
                ) : (
                  <User className="h-6 w-6 text-volt" />
                )}
              </div>
              <div>
                <p className="font-medium">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
